/**
 * @lodex-pro/web-portal/auth/tenant-type-context — portal tenant-type React context.
 *
 * Phase 16 / DEC-159 — web-portal serves BOTH operator and disposal tenants. The
 * Amplify Cognito pool is chosen at page load in `main.tsx` from the URL path
 * (`/portal/disposal/*` → disposal pool; everything else → operator pool). Once
 * the app tree mounts, nested components (branding on `SignInPage`, post-signin
 * navigation targets) need to know which tenant type is active without re-parsing
 * `window.location.pathname` themselves. This context is the single source of
 * truth inside the React tree; `App.tsx` provides it per route subtree.
 *
 * NOTE: This context is intentionally lightweight — the pool decision has ALREADY
 * been made by `main.tsx` (module scope) before any component mounts. This context
 * exists purely so downstream consumers (SignInPage branding, disposal-tree
 * landing page) can render the correct copy + nav target without re-plumbing
 * props through PortalShell.
 *
 * Type: `PortalTenantType = 'operator' | 'disposal'`. Web-portal is intentionally
 * narrower than `AmplifyTenantType` from `@lodex-pro/shared/auth/amplify-config`
 * (which includes `hauler` + `platform`) because those two tenant types are
 * served by other apps (mobile + web-admin respectively).
 *
 * Spec: DEC-159; DEC-152 (SignInPage branding); DEC-155 (public route pattern).
 */

import React, { createContext, useContext } from 'react';

export type PortalTenantType = 'operator' | 'disposal';

const PortalTenantTypeContext = createContext<PortalTenantType | null>(null);

export interface PortalTenantTypeProviderProps {
  readonly tenantType: PortalTenantType;
  readonly children: React.ReactNode;
}

export function PortalTenantTypeProvider({
  tenantType,
  children,
}: PortalTenantTypeProviderProps): React.ReactElement {
  return (
    <PortalTenantTypeContext.Provider value={tenantType}>
      {children}
    </PortalTenantTypeContext.Provider>
  );
}

/**
 * Read the active portal tenant type from context. Throws if invoked outside a
 * `PortalTenantTypeProvider` — the SignInPage + disposal home stub both MUST live
 * inside a provider (App.tsx routes wrap them). Throwing (rather than defaulting
 * to `'operator'`) surfaces a misconfiguration loudly instead of silently
 * mislabelling the disposal portal.
 */
export function usePortalTenantType(): PortalTenantType {
  const t = useContext(PortalTenantTypeContext);
  if (t === null) {
    throw new Error('usePortalTenantType must be used inside PortalTenantTypeProvider');
  }
  return t;
}
